<script lang="ts">
  import { Link, Text, type LinkProps } from '@squareup/public-web-design-system';
  import type { PwNextHeroProps, CTAKeyType } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import PwNextNoise from '../PwNextPartials/PwNextNoise/PwNextNoise.svelte';

  // Entry-based props
  export let background: PwNextHeroProps['background'] = undefined;
  export let body: PwNextHeroProps['body'] = undefined;
  export let buttonGroup: PwNextHeroProps['buttonGroup'] = [];
  export let eyebrow: PwNextHeroProps['eyebrow'] = undefined;
  export let headline: PwNextHeroProps['headline'] = undefined;
  export let video: PwNextHeroProps['video'] = undefined;
  // Option-based props
  export let colorMode: PwNextHeroProps['colorMode'] = undefined;
  export let contentAlignment: PwNextHeroProps['contentAlignment'] = undefined;
  export let headlineFitText: PwNextHeroProps['headlineFitText'] = undefined;
  export let headlineSize: PwNextHeroProps['headlineSize'] = undefined;
  export let videoDisplay: PwNextHeroProps['videoDisplay'] = undefined;
  export let fallbackBgColor: PwNextHeroProps['fallbackBgColor'];
  export let noise: PwNextHeroProps['noise'];

  function getLinkVariant(key: CTAKeyType = 'primaryCTA'): LinkProps['variant'] {
    const variantMap: Record<CTAKeyType, LinkProps['variant']> = {
      primaryCTA: 'button',
      secondaryCTA: 'button-alt',
    };

    return variantMap[key];
  }
</script>

<section
  data-color-mode={colorMode}
  class="hero color-mode--{colorMode} content-alignment--{contentAlignment}"
  class:withFallbackColor={Boolean(fallbackBgColor)}
  style:--fallback-bg-color={fallbackBgColor}
>
  <div
    class="hero-container"
    class:hero-container--video-as-background={video && videoDisplay === 'background'}
    class:hero-container--includes-background={background}
  >
    <div class="text-content">
      {#if eyebrow || headline}
        <h1>
          <!-- H1 should include both eyebrow and heading for SEO -->
          {#if eyebrow}
            <span class="hero-eyebrow">
              <!--
              RESPONSIVE CSS NOTE:
              When the variant is 100, it's set to be larger at medium sized screens and up.
              This is to deliver a small mobile eyebrow for the 1 alignment use case. -->
              <Text variant={contentAlignment === 'left' ? '100' : '200'} as="span">{eyebrow}</Text>
            </span>
          {/if}
          {#if headline}
            <span
              class="hero-headline"
              class:hero-headline--fit-text={headlineFitText && headlineFitText > 0}
              style:--headline-fit-text={headlineFitText && headlineFitText > 0 ? headlineFitText : null}
            >
              <Text variant={headlineSize} as="span">{headline}</Text>
            </span>
          {/if}
        </h1>
      {/if}
      {#if body}
        <div class="hero-body">
          <Text variant="600">{body}</Text>
        </div>
      {/if}
      {#if buttonGroup.length > 0}
        <div class="button-container">
          {#each buttonGroup as button}
            <Link
              variant={getLinkVariant(button.props?.key)}
              {colorMode}
              href={button.props?.url}
              text={button.props?.text}
            />
          {/each}
        </div>
      {/if}
    </div>
    {#if video}
      <div class="video-content video-display--{videoDisplay}">
        <DynamicChildrenComponent children={[video]} />
      </div>
    {/if}
  </div>
  {#if background}
    <div class="background-container">
      <DynamicChildrenComponent children={[background]} />
      {#if noise.useOverlay}
        <PwNextNoise
          noiseImage={noise.noiseImage}
          opacity={noise.noiseSettings?.opacity ?? '.4'}
          size={noise.noiseSettings?.size ?? '120px'}
        />
      {/if}
    </div>
  {/if}
</section>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;
  @use '@squareup/public-web-design-system/dist/styles/mixins/_text-style' as textStyle;

  @layer custom-components {
    .hero {
      display: grid;
      grid-template-columns: 1fr;
      gap: var(--space-80);
      overflow: hidden;

      // This value represents the amount of relative horizontal space needed
      // to optically align left-aligned text with the left edge of our grid system.
      --optical-left-align-text-indent: -0.1ch;
    }

    .hero-container {
      padding: var(--space-80) 0;
      margin: 0 auto;
      width: var(--container-width-current);
      text-align: center;
      display: grid;
      grid-template-columns: 1fr;
      align-items: center;
      grid-column: 1 / -1;
      grid-row: 1 / -1;
      z-index: 1;

      @include mediaQueries.at('medium') {
        padding: var(--space-100) 0;
      }

      .content-alignment--left & {
        text-align: left;
        padding: var(--space-50) 0;

        @include mediaQueries.at('medium') {
          padding: var(--space-100) 0;
        }
      }

      &.hero-container--video-as-background {
        padding: var(--space-20) 0;
        @include mediaQueries.at('medium') {
          padding: var(--space-40) 0 var(--space-30);
        }
      }
      &.hero-container--includes-background {
        padding-bottom: 0;
      }
    }

    .text-content {
      display: flex;
      flex-direction: column;
      grid-column: 1 / -1;
      grid-row: 1 / -1;
      z-index: 1;
      height: 100%;
      justify-content: center;
      container-type: inline-size; // This containment context powers the fit-text feature

      .content-alignment--left & {
        justify-content: flex-start;
      }

      .hero-container--video-as-background & {
        padding: var(--space-20);

        @include mediaQueries.at('medium') {
          padding: var(--space-40);
        }
      }

      :global(h1) {
        // need to prevent global styles from affecting the h1 children spans
        line-height: 0;
        margin: 0;
      }

      .hero-eyebrow {
        display: block;

        .content-alignment--left & {
          @include mediaQueries.at('medium') {
            :global(.Text) {
              @include textStyle.apply('200');
            }
          }
        }
      }

      .hero-headline {
        margin: 0 auto;
        display: block;

        .content-alignment--left & {
          max-width: 100%;
        }

        :global {
          .Text {
            display: block;
            margin-top: var(--space-20);

            @include mediaQueries.at('medium') {
              margin-top: var(--space-40);
            }

            .content-alignment--left & {
              text-indent: var(--optical-left-align-text-indent);
            }
          }

          .Text-variant--900 {
            margin-top: var(--space-10);
            @include mediaQueries.at('medium') {
              margin-top: var(--space-20);
            }
          }
        }

        &--fit-text {
          max-width: 100%;
          :global(.Text) {
            line-height: 1;
            margin-top: 0; // prevent other headline styles from adding margin

            // FIT TEXT: makes the font-size responsive to the width of the container
            // --headline-fit-text is a unitless number that will be used to calculate the font-size
            // when --headline-fit-text is 1000, the font-size will be similar to Text variant 1000
            // large numbers will result in a large font size
            // small numbers will result in a small font size
            font-size: calc(
              0.01cqw * var(--headline-fit-text)
            ); // 0.01cqw is a unit that is 1% of the containment context's width
          }
        }
      }

      .hero-body {
        margin-top: var(--space-10);

        @include mediaQueries.at('medium') {
          margin-top: var(--space-20);
        }

        @include mediaQueries.at('large') {
          margin-top: var(--space-30);
        }

        .content-alignment--left & {
          text-indent: var(--optical-left-align-text-indent);
        }
      }

      .button-container {
        display: flex;
        gap: var(--space-20);
        margin: var(--space-30) auto 0;
        flex-wrap: wrap;
        justify-content: center;

        @include mediaQueries.at('medium') {
          margin-top: var(--space-60);
        }
        .content-alignment--left & {
          margin-left: 0;
          margin-right: 0;
          justify-content: flex-start;
        }
      }
    }

    .video-content {
      border-radius: 1rem;
      overflow: hidden;
      position: relative;

      :global(.VideoComponent) {
        height: 100%; // this makes the video fill container
      }
    }

    .video-display--foreground {
      margin-top: var(--space-80);
    }

    .video-display--background {
      grid-column: 1 / -1;
      grid-row: 1 / -1;
      height: 100%;
      :global(.BoxComponent) {
        height: 100%;
      }

      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.42); // this is the video overlay color that ensure text is readable
      }
    }

    .background-container {
      grid-column: 1 / -1;
      grid-row: 1 / -1;
      position: relative;
      &::after {
        content: '';
        position: absolute;
        inset: auto 0 0;
        height: 50%;
        background: linear-gradient(0deg, #fff 28.5%, rgba(255, 255, 255, 0) 88.5%); // pulled from Figma
      }

      .color-mode--dark & {
        // this ensure that white text will be readable if the background image is slow to load or fails to load
        background: var(--color-neutral-400);
      }
      .withFallbackColor & {
        background-color: var(--fallback-bg-color);
      }

      :global {
        .AproposImageComponent {
          position: absolute;
          inset: 0;
          height: 100%;
        }
        .AproposImageComponent .ResponsiveImagePartial {
          height: 100%;
        }

        .AproposImageComponent img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
    }
  }
</style>
